import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Maximize2, Minimize2 } from "lucide-react";

import { LivePdfPreview } from "./pdfPreview";
import { interp, matchAnchors } from "./scrollSync";

/**
 * The MOU document / template editor's working area:
 *
 *  - the editor, at a height the user drags (remembered per editor);
 *  - optionally the live PDF beside it, at the same height (Live preview),
 *    scrolling along with the editor in both directions, paragraph to paragraph;
 *  - a full-screen mode for long documents (Esc leaves it).
 *
 * `children` is a render function ({ height, onCursorPage }) => editor, so the
 * editor fills whatever height the workspace gives it and reports which page
 * the cursor is on, which the preview opens at.
 */

const MIN_H = 260;
const store = {
  get(key, fallback) {
    try { const v = window.localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
    catch { return fallback; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
  },
};

export default function EditorWorkspace({ storageKey, buildPreview, contentKey, defaultHeight = 560, children }) {
  const hKey = `mou-editor-height:${storageKey}`;
  const [height, setHeight] = useState(() => Math.max(MIN_H, Number(store.get(hKey, defaultHeight)) || defaultHeight));
  const [live, setLive] = useState(() => store.get("mou-live-preview", typeof window !== "undefined" && window.innerWidth >= 1280));
  const [full, setFull] = useState(false);
  const [viewportH, setViewportH] = useState(typeof window !== "undefined" ? window.innerHeight : 900);
  const [cursorPage, setCursorPage] = useState(1);
  const drag = useRef(null);

  useEffect(() => { store.set("mou-live-preview", !!live); }, [live]);

  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    onResize();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = prev;
    };
  }, [full]);

  const onHandleDown = (e) => {
    e.preventDefault();
    drag.current = { y: e.clientY, h: height };
    const move = (ev) => {
      const next = Math.min(Math.max(drag.current.h + ev.clientY - drag.current.y, MIN_H), 3000);
      setHeight(next);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      setHeight((h) => { store.set(hKey, h); return h; });
      drag.current = null;
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const effectiveH = full ? Math.max(MIN_H, viewportH - 120) : height;
  const onCursorPage = useCallback((p) => setCursorPage(p), []);

  // Scroll the editor and the PDF together, either way, paragraph by
  // paragraph: each editor paragraph is matched to the same text in the PDF
  // (scrollSync.js) and positions in between are interpolated. Until any text
  // matches, it goes by how far down each one is. A pane we just moved ignores
  // its own scroll events for a moment, so the two don't chase each other.
  const editorPane = useRef(null);
  const pdfBox = useRef(null);
  const pdfLines = useRef([]);
  const anchors = useRef({ key: "", pairs: [] });
  const moved = useRef({ el: null, until: 0 });
  const editorScroller = () => {
    const pane = editorPane.current;
    if (!pane) return null;
    return [...pane.querySelectorAll(".ql-editor, .ql-container")]
      .find((el) => el.scrollHeight > el.clientHeight + 1) || null;
  };
  // [[editorY, pdfY], …] in each scroller's content coordinates; rebuilt when
  // either side's layout changes.
  const anchorPairs = (ed, pdf) => {
    const root = editorPane.current?.querySelector(".ql-editor");
    if (!root || !pdfLines.current.length) return [];
    const key = `${pdfLines.current.length}|${ed.scrollHeight}|${root.childElementCount}|${pdf.scrollHeight}|${pdf.clientWidth}`;
    if (anchors.current.key === key) return anchors.current.pairs;
    const edTop = ed.getBoundingClientRect().top - ed.scrollTop;
    const blocks = [];
    for (const el of root.children) {
      for (const part of el.tagName === "TABLE" ? el.querySelectorAll("tr") : [el]) {
        blocks.push({ text: part.textContent, y: part.getBoundingClientRect().top - edTop });
      }
    }
    const pages = new Map([...pdf.querySelectorAll("canvas[data-page]")].map((c) => [Number(c.dataset.page), c]));
    const lines = pdfLines.current
      .filter((l) => pages.has(l.page))
      .map((l) => { const c = pages.get(l.page); return { text: l.text, y: c.offsetTop + l.frac * c.clientHeight }; });
    const pairs = matchAnchors(blocks, lines);
    // Both at the top together, so page 1's letterhead shows above the text.
    if (pairs.length && pairs[0][0] > 0 && pairs[0][1] > 0) pairs.unshift([0, 0]);
    anchors.current = { key, pairs };
    return pairs;
  };
  const follow = (from, to) => {
    if (!from || !to) return;
    if (moved.current.el === from && performance.now() < moved.current.until) return;
    const fromMax = from.scrollHeight - from.clientHeight;
    const toMax = to.scrollHeight - to.clientHeight;
    if (toMax <= 0) return;
    const fromPdf = from === pdfBox.current;
    const pairs = fromPdf ? anchorPairs(to, from).map(([e, p]) => [p, e]) : anchorPairs(from, to);
    const top = Math.round(Math.min(toMax, Math.max(0, pairs.length >= 2
      ? interp(pairs, from.scrollTop)
      : (fromMax > 0 ? from.scrollTop / fromMax : 0) * toMax)));
    if (Math.abs(to.scrollTop - top) < 1) return;
    moved.current = { el: to, until: performance.now() + 120 };
    to.scrollTop = top;
  };
  const onEditorScroll = (e) => {
    if (live && e.target.matches?.(".ql-editor, .ql-container")) follow(e.target, pdfBox.current);
  };
  const onPdfScroll = () => follow(pdfBox.current, editorScroller());
  const onPdfRendered = (lines) => {
    pdfLines.current = lines || [];
    anchors.current = { key: "", pairs: [] };
    follow(editorScroller(), pdfBox.current);
  };

  const body = (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
        {full && <span className="mr-auto text-[11px] text-gray-500">Full screen — press Esc to leave</span>}
        <button type="button" onClick={() => setLive((v) => !v)} aria-pressed={!!live}
                className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 ${live
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"}`}>
          {live ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} Live preview
        </button>
        <button type="button" onClick={() => setFull((v) => !v)}
                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
          {full ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />} {full ? "Exit full screen" : "Full screen"}
        </button>
      </div>
      <div className={live ? "grid gap-3 xl:grid-cols-2" : ""}>
        <div ref={editorPane} onScrollCapture={onEditorScroll} className="min-w-0">
          {children({ height: effectiveH, onCursorPage })}
        </div>
        {live && (
          <LivePdfPreview build={buildPreview} contentKey={contentKey} page={cursorPage}
                          height={effectiveH + 44} scrollerRef={pdfBox}
                          onScroll={onPdfScroll} onRendered={onPdfRendered} />
        )}
      </div>
      {!full && (
        <div role="separator" aria-orientation="horizontal" aria-label="Drag to change the editor height"
             title="Drag to change the editor height" onMouseDown={onHandleDown}
             className="flex h-3 cursor-row-resize items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <span className="h-1 w-12 rounded-full bg-gray-300 dark:bg-gray-500" />
        </div>
      )}
    </div>
  );

  // One wrapper whatever the mode: swapping elements would remount the editor
  // and lose its undo history and cursor.
  return (
    <div className={full ? "fixed inset-0 z-[1500] overflow-auto bg-white p-3 dark:bg-gray-900" : ""}
         data-testid={full ? "editor-fullscreen" : "editor-workspace"}>
      {body}
    </div>
  );
}
